import { Inject, Injectable } from '@angular/core';
import { IPublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MSAL_INSTANCE } from './constants';

interface MsalBrowserStorageShape {
  cleanRequestByInteractionType?: (type: InteractionType) => void;
  setInteractionInProgress?: (inProgress: boolean) => void;
  clearMsalCookies?: () => void;
}

interface MsalInstanceWithInternals {
  browserStorage?: MsalBrowserStorageShape;
}

@Injectable({
  providedIn: 'root',
})
export class MsalInteractionRecoveryService {
  constructor(@Inject(MSAL_INSTANCE) private readonly msalInstance: IPublicClientApplication) {}

  clearStaleInteractionState(): void {
    const cleared = this.clearViaBrowserStorage();
    if (!cleared) {
      this.clearViaCookieSweep();
    }
  }

  private clearViaBrowserStorage(): boolean {
    // MSAL v2 does not expose interaction-lock cleanup on IPublicClientApplication.
    // BrowserCacheManager.cleanRequestByInteractionType is the same call MSAL itself
    // makes from handleRedirectPromise when it detects a stale flag.
    const storage = (this.msalInstance as unknown as MsalInstanceWithInternals).browserStorage;
    if (!storage) {
      return false;
    }
    try {
      if (typeof storage.cleanRequestByInteractionType === 'function') {
        storage.cleanRequestByInteractionType(InteractionType.Redirect);
        return true;
      }
      if (typeof storage.setInteractionInProgress === 'function') {
        storage.setInteractionInProgress(false);
        if (typeof storage.clearMsalCookies === 'function') {
          storage.clearMsalCookies();
        }
        return true;
      }
    } catch (err) {
      console.warn('[MsalInteractionRecovery] browserStorage cleanup failed, falling back to cookie sweep', err);
      return false;
    }
    return false;
  }

  private clearViaCookieSweep(): void {
    const clientId = this.getClientId();
    if (!clientId) {
      console.warn('[MsalInteractionRecovery] cookie sweep skipped: clientId unavailable');
      return;
    }
    const prefix = `msal.${clientId}.`;
    const encodedPrefix = encodeURIComponent(prefix);
    const secureAttr = window.location.protocol === 'https:' ? ' Secure;' : '';

    document.cookie.split(';').forEach((rawCookie) => {
      const cookie = rawCookie.trim();
      if (!cookie) {
        return;
      }
      const eqIdx = cookie.indexOf('=');
      const rawName = eqIdx === -1 ? cookie : cookie.substring(0, eqIdx);
      if (!rawName.startsWith(encodedPrefix) && !rawName.startsWith(prefix)) {
        return;
      }
      document.cookie = `${rawName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax;${secureAttr}`;
    });
  }

  private getClientId(): string | null {
    try {
      return this.msalInstance.getConfiguration().auth.clientId || null;
    } catch {
      return null;
    }
  }
}
